"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TermPlanningGrid = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * TermPlanningGrid component - drag-and-drop grid for planning terms and courses
 */
const react_1 = require("react");
const core_1 = require("@dnd-kit/core");
const sortable_1 = require("@dnd-kit/sortable");
const utilities_1 = require("@dnd-kit/utilities");
const course_1 = require("../models/course");
const Select_1 = require("./Select");
const Button_1 = require("./Button");
require("./TermPlanningGrid.scss");
/** Searchable course picker used by the compact student-plan layout. */
const SearchableCoursePicker = ({ courses, disabled = false, termName, onSelect, }) => {
    const [isOpen, setIsOpen] = (0, react_1.useState)(false);
    const [query, setQuery] = (0, react_1.useState)('');
    const [activeIndex, setActiveIndex] = (0, react_1.useState)(0);
    const [isKeyboardNavigating, setIsKeyboardNavigating] = (0, react_1.useState)(false);
    const pickerRef = (0, react_1.useRef)(null);
    const inputRef = (0, react_1.useRef)(null);
    const activeOptionRef = (0, react_1.useRef)(null);
    const instanceId = (0, react_1.useId)().replace(/:/g, '');
    const inputId = `course-search-${instanceId}`;
    const listboxId = `course-results-${instanceId}`;
    const filteredCourses = (0, react_1.useMemo)(() => {
        const normalizedQuery = query.trim().toLocaleLowerCase();
        if (!normalizedQuery)
            return courses;
        return courses.filter((course) => [
            course.code,
            course.name,
            course.displayCode,
            course.displayName,
            course_1.CourseHelpers.getDisplayLabel(course),
        ]
            .filter(Boolean)
            .some((value) => value.toLocaleLowerCase().includes(normalizedQuery)));
    }, [courses, query]);
    (0, react_1.useEffect)(() => {
        var _a;
        if (!isOpen)
            return;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        const handleOutsideClick = (event) => {
            var _a;
            if (!((_a = pickerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                setIsOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen]);
    (0, react_1.useEffect)(() => {
        setActiveIndex(0);
        setIsKeyboardNavigating(false);
    }, [query, isOpen]);
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (!isOpen)
            return;
        (_b = (_a = activeOptionRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, {
            block: 'nearest',
            inline: 'nearest',
        });
    }, [activeIndex, filteredCourses, isOpen]);
    const closePicker = () => {
        setIsOpen(false);
        setQuery('');
    };
    const selectCourse = (course) => {
        onSelect(course.id);
        closePicker();
    };
    const handleSearchKeyDown = (event) => {
        var _a;
        if (event.key === 'Escape') {
            event.preventDefault();
            closePicker();
            return;
        }
        if (filteredCourses.length === 0)
            return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setIsKeyboardNavigating(true);
            setActiveIndex((current) => (current + 1) % filteredCourses.length);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setIsKeyboardNavigating(true);
            setActiveIndex((current) => current === 0 ? filteredCourses.length - 1 : current - 1);
        }
        else if (event.key === 'Enter') {
            event.preventDefault();
            const selectedCourse = (_a = filteredCourses.at(activeIndex)) !== null && _a !== void 0 ? _a : filteredCourses.at(0);
            if (selectedCourse)
                selectCourse(selectedCourse);
        }
    };
    const noCoursesAvailable = courses.length === 0;
    return ((0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__course-picker", ref: pickerRef, children: [(0, jsx_runtime_1.jsxs)("button", { type: "button", className: "term-planning-grid__add-course-trigger", onClick: () => setIsOpen((open) => !open), disabled: disabled || noCoursesAvailable, "aria-label": `Add Course to ${termName}`, "aria-expanded": isOpen, "aria-haspopup": "listbox", "aria-controls": isOpen ? listboxId : undefined, children: [(0, jsx_runtime_1.jsx)("span", { "aria-hidden": "true", children: "+" }), noCoursesAvailable ? 'No Courses Available' : 'Add Course'] }), isOpen && ((0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__course-picker-popover", children: [(0, jsx_runtime_1.jsxs)("label", { className: "term-planning-grid__visually-hidden", htmlFor: inputId, children: ["Search courses for ", termName] }), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__course-search-wrap", children: [(0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-search-icon", "aria-hidden": "true", children: "\u2315" }), (0, jsx_runtime_1.jsx)("input", { ref: inputRef, id: inputId, type: "search", className: "term-planning-grid__course-search", placeholder: "Search by course code or title...", value: query, onChange: (event) => setQuery(event.target.value), onKeyDown: handleSearchKeyDown, role: "combobox", "aria-autocomplete": "list", "aria-expanded": "true", "aria-controls": listboxId, "aria-activedescendant": filteredCourses.length > 0
                                    ? `${listboxId}-option-${activeIndex}`
                                    : undefined }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "term-planning-grid__course-picker-close", onClick: closePicker, "aria-label": "Close course search", children: "\u00D7" })] }), (0, jsx_runtime_1.jsx)("ul", { className: "term-planning-grid__course-results", id: listboxId, role: "listbox", "data-keyboard-navigation": isKeyboardNavigating ? 'true' : undefined, children: filteredCourses.length === 0 ? ((0, jsx_runtime_1.jsx)("li", { className: "term-planning-grid__course-results-empty", children: "No courses match this search." })) : (filteredCourses.map((course, index) => ((0, jsx_runtime_1.jsx)("li", { role: "presentation", children: (0, jsx_runtime_1.jsxs)("button", { ref: index === activeIndex ? activeOptionRef : undefined, id: `${listboxId}-option-${index}`, type: "button", className: "term-planning-grid__course-result", role: "option", "aria-selected": index === activeIndex, tabIndex: -1, onMouseMove: () => {
                                    setIsKeyboardNavigating(false);
                                    setActiveIndex(index);
                                }, onMouseDown: (event) => event.preventDefault(), onClick: () => selectCourse(course), children: [(0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-result-code", children: course.displayCode || course.code }), (0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-result-name", children: course.displayName || course.name }), course.credits > 0 && ((0, jsx_runtime_1.jsxs)("span", { className: "term-planning-grid__course-result-credits", children: [course.credits, " cr"] }))] }) }, course.id)))) })] }))] }));
};
/** Searchable select used by planning controls that retain a separate Add action. */
const SearchablePlanningPicker = ({ options, value, label, placeholder, emptyPlaceholder, noResultsMessage, disabled = false, onChange, }) => {
    var _a;
    const [isOpen, setIsOpen] = (0, react_1.useState)(false);
    const [query, setQuery] = (0, react_1.useState)('');
    const [activeIndex, setActiveIndex] = (0, react_1.useState)(0);
    const [isKeyboardNavigating, setIsKeyboardNavigating] = (0, react_1.useState)(false);
    const pickerRef = (0, react_1.useRef)(null);
    const activeOptionRef = (0, react_1.useRef)(null);
    const instanceId = (0, react_1.useId)().replace(/:/g, '');
    const inputId = `planning-search-${instanceId}`;
    const listboxId = `planning-results-${instanceId}`;
    const selectedOption = options.find((option) => option.value === value);
    const selectedLabel = (_a = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) !== null && _a !== void 0 ? _a : '';
    const filteredOptions = (0, react_1.useMemo)(() => {
        // Reopening a selected value should show the full list so it can be changed.
        const filterValue = value && query === selectedLabel ? '' : query;
        const normalizedQuery = filterValue.trim().toLocaleLowerCase();
        if (!normalizedQuery)
            return options;
        return options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedQuery));
    }, [options, query, selectedLabel, value]);
    (0, react_1.useEffect)(() => {
        if (!isOpen)
            setQuery(selectedLabel);
    }, [isOpen, selectedLabel]);
    (0, react_1.useEffect)(() => {
        if (!isOpen)
            return;
        const handleOutsideClick = (event) => {
            var _a;
            if (!((_a = pickerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                setIsOpen(false);
                setQuery(selectedLabel);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, selectedLabel]);
    (0, react_1.useEffect)(() => {
        setActiveIndex(0);
        setIsKeyboardNavigating(false);
    }, [query, isOpen]);
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (!isOpen)
            return;
        (_b = (_a = activeOptionRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, {
            block: 'nearest',
            inline: 'nearest',
        });
    }, [activeIndex, filteredOptions, isOpen]);
    const closePicker = () => {
        setIsOpen(false);
        setQuery(selectedLabel);
    };
    const selectOption = (option) => {
        onChange(option.value);
        setQuery(option.label);
        setIsOpen(false);
    };
    const handleSearchKeyDown = (event) => {
        var _a;
        if (event.key === 'Escape') {
            event.preventDefault();
            closePicker();
            return;
        }
        if (filteredOptions.length === 0)
            return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setIsKeyboardNavigating(true);
            setActiveIndex((current) => (current + 1) % filteredOptions.length);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setIsKeyboardNavigating(true);
            setActiveIndex((current) => current === 0 ? filteredOptions.length - 1 : current - 1);
        }
        else if (event.key === 'Enter') {
            event.preventDefault();
            const selectedOption = (_a = filteredOptions.at(activeIndex)) !== null && _a !== void 0 ? _a : filteredOptions.at(0);
            if (selectedOption)
                selectOption(selectedOption);
        }
    };
    const noOptionsAvailable = options.length === 0;
    return ((0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__searchable-picker", ref: pickerRef, onBlur: (event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
                closePicker();
        }, children: [(0, jsx_runtime_1.jsx)("label", { className: "term-planning-grid__searchable-picker-label", htmlFor: inputId, children: label }), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__searchable-picker-control", children: [(0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__searchable-picker-icon", "aria-hidden": "true", children: "\u2315" }), (0, jsx_runtime_1.jsx)("input", { id: inputId, type: "text", className: "term-planning-grid__searchable-picker-input", placeholder: noOptionsAvailable ? emptyPlaceholder : placeholder, value: query, onFocus: (event) => {
                            setIsOpen(true);
                            if (value)
                                event.currentTarget.select();
                        }, onClick: () => setIsOpen(true), onChange: (event) => {
                            setQuery(event.target.value);
                            if (value)
                                onChange('');
                            setIsOpen(true);
                        }, onKeyDown: handleSearchKeyDown, disabled: disabled || noOptionsAvailable, role: "combobox", "aria-autocomplete": "list", "aria-expanded": isOpen, "aria-controls": isOpen ? listboxId : undefined, "aria-activedescendant": isOpen && filteredOptions.length > 0
                            ? `${listboxId}-option-${activeIndex}`
                            : undefined, autoComplete: "off" }), (0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__searchable-picker-chevron", "aria-hidden": "true", children: "\u2304" })] }), isOpen && ((0, jsx_runtime_1.jsx)("ul", { className: "term-planning-grid__searchable-picker-results", id: listboxId, role: "listbox", "data-keyboard-navigation": isKeyboardNavigating ? 'true' : undefined, children: filteredOptions.length === 0 ? ((0, jsx_runtime_1.jsx)("li", { className: "term-planning-grid__searchable-picker-results-empty", children: noResultsMessage })) : (filteredOptions.map((option, index) => ((0, jsx_runtime_1.jsx)("li", { role: "presentation", children: (0, jsx_runtime_1.jsx)("button", { ref: index === activeIndex ? activeOptionRef : undefined, id: `${listboxId}-option-${index}`, type: "button", className: "term-planning-grid__searchable-picker-result", role: "option", "aria-selected": index === activeIndex, tabIndex: -1, onMouseMove: () => {
                            setIsKeyboardNavigating(false);
                            setActiveIndex(index);
                        }, onMouseDown: (event) => event.preventDefault(), onClick: () => selectOption(option), children: option.label }) }, option.value)))) }))] }));
};
/**
 * Sortable term row component
 */
const SortableTermRow = ({ termPlan, index, availableCourses, onAddCourse, onRemoveCourse, onToggleFailed, onRemoveTerm, disabled, compact = false, searchableCourseSelect = false, }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = (0, sortable_1.useSortable)({
        id: termPlan.id,
    });
    const [selectedCourseId, setSelectedCourseId] = (0, react_1.useState)('');
    const style = {
        transform: utilities_1.CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    const handleAddCourse = () => {
        if (selectedCourseId) {
            onAddCourse(termPlan.id, selectedCourseId);
            setSelectedCourseId('');
        }
    };
    const selectableCourses = availableCourses.filter((course) => !termPlan.courses.some((c) => c.course.id === course.id));
    const courseOptions = selectableCourses.map((course) => ({
        value: course.id,
        label: course_1.CourseHelpers.getDisplayLabel(course),
    }));
    const renderCourse = (coursePlan) => {
        const isLOA = course_1.CourseHelpers.isLOA(coursePlan.course);
        const isElective = course_1.CourseHelpers.isElective(coursePlan.course);
        const isInactive = course_1.CourseHelpers.isInactive(coursePlan.course);
        const isFailed = coursePlan.failed;
        const courseClasses = [
            'term-planning-grid__course-item',
            isLOA && 'term-planning-grid__course-item--loa',
            isElective && 'term-planning-grid__course-item--elective',
            isInactive && 'term-planning-grid__course-item--inactive',
            isFailed && 'term-planning-grid__course-item--failed',
        ].filter(Boolean).join(' ');
        return ((0, jsx_runtime_1.jsxs)("li", { className: courseClasses, children: [(0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-code", children: coursePlan.course.code }), (0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-name", title: coursePlan.course.name, children: coursePlan.course.name }), coursePlan.course.credits > 0 && ((0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__course-credits", children: coursePlan.course.credits })), !isLOA && onToggleFailed && ((0, jsx_runtime_1.jsx)("button", { type: "button", className: "term-planning-grid__toggle-failed", onClick: () => onToggleFailed(termPlan.id, coursePlan.id), disabled: disabled, "aria-label": isFailed ? `Mark ${coursePlan.course.name} as not failed` : `Mark ${coursePlan.course.name} as failed`, title: isFailed ? 'Mark as not failed' : 'Mark as failed', children: "\u270B" })), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "term-planning-grid__remove-course", onClick: () => onRemoveCourse(termPlan.id, coursePlan.id), disabled: disabled, "aria-label": `Remove ${coursePlan.course.name}`, children: "\u00D7" })] }, coursePlan.id));
    };
    const removeTermButton = ((0, jsx_runtime_1.jsx)("button", { type: "button", className: "term-planning-grid__remove-term", onClick: () => onRemoveTerm(termPlan.id), disabled: disabled, "aria-label": `Remove ${termPlan.term.name}`, children: "\u00D7" }));
    const termRowClasses = [
        'term-planning-grid__term-row',
        compact && 'term-planning-grid__term-row--compact',
        isDragging && 'term-planning-grid__term-row--dragging',
    ].filter(Boolean).join(' ');
    return ((0, jsx_runtime_1.jsxs)("div", { ref: setNodeRef, style: style, className: termRowClasses, children: [(0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__term-header", children: [(0, jsx_runtime_1.jsx)("button", Object.assign({ type: "button", className: "term-planning-grid__drag-handle" }, attributes, listeners, { disabled: disabled, "aria-label": `Drag to reorder ${termPlan.term.name}`, children: "\u22EE\u22EE" })), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__term-info", children: [(0, jsx_runtime_1.jsxs)("span", { className: "term-planning-grid__term-position", children: ["Term ", index + 1] }), (0, jsx_runtime_1.jsx)("span", { className: "term-planning-grid__term-name", children: termPlan.term.name })] }), !compact && removeTermButton] }), (0, jsx_runtime_1.jsx)("div", { className: "term-planning-grid__courses", children: compact ? ((0, jsx_runtime_1.jsxs)("ul", { className: "term-planning-grid__course-list", children: [termPlan.courses.map(renderCourse), termPlan.courses.length === 0 && ((0, jsx_runtime_1.jsx)("li", { className: "term-planning-grid__no-courses", children: "No courses added" })), (0, jsx_runtime_1.jsx)("li", { className: "term-planning-grid__add-course-slot", children: (0, jsx_runtime_1.jsx)(SearchableCoursePicker, { courses: selectableCourses, disabled: disabled, termName: termPlan.term.name, onSelect: (courseId) => onAddCourse(termPlan.id, courseId) }) })] })) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [termPlan.courses.length === 0 ? ((0, jsx_runtime_1.jsx)("p", { className: "term-planning-grid__no-courses", children: "No courses added" })) : ((0, jsx_runtime_1.jsx)("ul", { className: "term-planning-grid__course-list", children: termPlan.courses.map(renderCourse) })), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__add-course", children: [searchableCourseSelect ? ((0, jsx_runtime_1.jsx)(SearchablePlanningPicker, { label: "Add Course", value: selectedCourseId, options: courseOptions, disabled: disabled || courseOptions.length === 0, placeholder: "Search or select a course", emptyPlaceholder: "No courses available", noResultsMessage: "No courses match this search.", onChange: setSelectedCourseId })) : ((0, jsx_runtime_1.jsx)(Select_1.Select, { id: `course-select-${termPlan.id}`, label: "Add Course", value: selectedCourseId, onChange: (e) => setSelectedCourseId(e.target.value), options: courseOptions, disabled: disabled || courseOptions.length === 0, placeholder: courseOptions.length === 0 ? 'No courses available' : 'Select a course' })), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleAddCourse, disabled: disabled || !selectedCourseId, size: "small", variant: "secondary", children: "Add" })] })] })) }), compact && removeTermButton] }));
};
/**
 * TermPlanningGrid component for planning degree plan terms and courses
 * @param props - TermPlanningGridProps
 * @returns TermPlanningGrid component
 */
const TermPlanningGrid = ({ termPlans, availableTerms, availableCourses, onChange, onToggleFailed, disabled = false, compact = false, searchableCourseSelect = false, }) => {
    const [selectedTermId, setSelectedTermId] = (0, react_1.useState)('');
    const sensors = (0, core_1.useSensors)((0, core_1.useSensor)(core_1.PointerSensor), (0, core_1.useSensor)(core_1.KeyboardSensor, {
        coordinateGetter: sortable_1.sortableKeyboardCoordinates,
    }));
    const handleDragEnd = (0, react_1.useCallback)((event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = termPlans.findIndex((tp) => tp.id === active.id);
            const newIndex = termPlans.findIndex((tp) => tp.id === over.id);
            const reordered = (0, sortable_1.arrayMove)(termPlans, oldIndex, newIndex).map((tp, idx) => (Object.assign(Object.assign({}, tp), { position: idx + 1 })));
            onChange(reordered);
        }
    }, [termPlans, onChange]);
    const handleAddTerm = (0, react_1.useCallback)(() => {
        if (!selectedTermId)
            return;
        const term = availableTerms.find((t) => t.id === selectedTermId);
        if (!term)
            return;
        const newTermPlan = {
            id: `term-plan-${Date.now()}`,
            term,
            position: termPlans.length + 1,
            courses: [],
        };
        onChange([...termPlans, newTermPlan]);
        setSelectedTermId('');
    }, [selectedTermId, availableTerms, termPlans, onChange]);
    const handleAddCourse = (0, react_1.useCallback)((termId, courseId) => {
        const course = availableCourses.find((c) => c.id === courseId);
        if (!course)
            return;
        const updated = termPlans.map((tp) => {
            if (tp.id === termId) {
                return Object.assign(Object.assign({}, tp), { courses: [
                        ...tp.courses,
                        {
                            id: `course-plan-${Date.now()}-${courseId}`,
                            course,
                            failed: false,
                        },
                    ] });
            }
            return tp;
        });
        onChange(updated);
    }, [availableCourses, termPlans, onChange]);
    const handleRemoveCourse = (0, react_1.useCallback)((termId, courseId) => {
        const updated = termPlans.map((tp) => {
            if (tp.id === termId) {
                return Object.assign(Object.assign({}, tp), { courses: tp.courses.filter((c) => c.id !== courseId) });
            }
            return tp;
        });
        onChange(updated);
    }, [termPlans, onChange]);
    const handleRemoveTerm = (0, react_1.useCallback)((termId) => {
        const updated = termPlans
            .filter((tp) => tp.id !== termId)
            .map((tp, idx) => (Object.assign(Object.assign({}, tp), { position: idx + 1 })));
        onChange(updated);
    }, [termPlans, onChange]);
    // Non-special courses already placed in any term — excluded globally from all dropdowns.
    // Failed courses are excluded from this set so they can be re-added to a future term.
    const usedNonSpecialIds = (0, react_1.useMemo)(() => {
        const ids = new Set();
        termPlans.forEach((tp) => tp.courses.forEach((c) => {
            if (!course_1.CourseHelpers.isSpecial(c.course) && !c.failed)
                ids.add(c.course.id);
        }));
        return ids;
    }, [termPlans]);
    // Approved courses sorted: special courses first, then alphabetically by displayCode
    const filteredCourses = (0, react_1.useMemo)(() => {
        return availableCourses
            .filter((c) => c.approved !== false && !usedNonSpecialIds.has(c.id))
            .sort((a, b) => {
            const aSpecial = course_1.CourseHelpers.isSpecial(a);
            const bSpecial = course_1.CourseHelpers.isSpecial(b);
            if (aSpecial !== bSpecial)
                return aSpecial ? -1 : 1;
            const aCode = a.displayCode || a.code;
            const bCode = b.displayCode || b.code;
            return aCode.localeCompare(bCode);
        });
    }, [availableCourses, usedNonSpecialIds]);
    // Terms sorted chronologically, excluding already-added terms
    const termOptions = (0, react_1.useMemo)(() => availableTerms
        .filter((term) => !termPlans.some((tp) => tp.term.id === term.id))
        .sort((a, b) => {
        const diff = a.startDate.getTime() - b.startDate.getTime();
        return diff !== 0 ? diff : a.endDate.getTime() - b.endDate.getTime();
    })
        .map((term) => ({
        value: term.id,
        label: term.name,
    })), [availableTerms, termPlans]);
    return ((0, jsx_runtime_1.jsxs)("div", { className: `term-planning-grid${compact ? ' term-planning-grid--compact' : ''}`, children: [(0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__header", children: [(0, jsx_runtime_1.jsx)("h3", { className: "term-planning-grid__title", children: "Term Planning" }), (0, jsx_runtime_1.jsx)("p", { className: "term-planning-grid__description", children: "Add terms and assign courses to each term" })] }), (0, jsx_runtime_1.jsx)(core_1.DndContext, { sensors: sensors, collisionDetection: core_1.closestCenter, onDragEnd: handleDragEnd, children: (0, jsx_runtime_1.jsx)(sortable_1.SortableContext, { items: termPlans.map((tp) => tp.id), strategy: sortable_1.verticalListSortingStrategy, children: (0, jsx_runtime_1.jsx)("div", { className: "term-planning-grid__terms", children: termPlans.length === 0 ? ((0, jsx_runtime_1.jsx)("div", { className: "term-planning-grid__empty", children: (0, jsx_runtime_1.jsx)("p", { children: "No terms added yet. Add a term to get started." }) })) : (termPlans.map((termPlan, index) => ((0, jsx_runtime_1.jsx)(SortableTermRow, { termPlan: termPlan, index: index, availableCourses: filteredCourses, onAddCourse: handleAddCourse, onRemoveCourse: handleRemoveCourse, onToggleFailed: onToggleFailed, onRemoveTerm: handleRemoveTerm, disabled: disabled, compact: compact, searchableCourseSelect: searchableCourseSelect }, termPlan.id)))) }) }) }), (0, jsx_runtime_1.jsxs)("div", { className: "term-planning-grid__add-term", children: [(0, jsx_runtime_1.jsx)(SearchablePlanningPicker, { label: "Add Term", value: selectedTermId, options: termOptions, disabled: disabled || termOptions.length === 0, placeholder: "Search or select a term", emptyPlaceholder: "No terms available", noResultsMessage: "No terms match this search.", onChange: setSelectedTermId }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleAddTerm, disabled: disabled || !selectedTermId, variant: "secondary", children: "Add Term" })] })] }));
};
exports.TermPlanningGrid = TermPlanningGrid;
exports.default = exports.TermPlanningGrid;
